/**
 * Canonical in-app document URLs use the `/d` prefix (view / history / files + list).
 * Legacy `/documents/...` routes redirect to these paths with replace; parsing supports both.
 */

const LEGACY_PREFIX = '/documents';
const APP_PREFIX = '/d';

const DOCUMENTS_FILES_SUFFIX = '/files';

/** Encode a document path for use in a path segment (slash-separated). */
export function encodeDocumentPathForUrl(path: string): string {
  return path
    .split('/')
    .map((p) => encodeURIComponent(p))
    .join('/');
}

/**
 * Parse the suffix after {@link LEGACY_PREFIX}/ (e.g. `a/b` or `a/b/history`).
 * Same rules as the former `document-page` router.
 */
export function parseDocumentLocationSuffix(fullPath: string): {
  documentPath?: string;
  isHistoryMode: boolean;
  isFilesPage: boolean;
} {
  if (
    fullPath.endsWith(DOCUMENTS_FILES_SUFFIX) &&
    fullPath.length > DOCUMENTS_FILES_SUFFIX.length
  ) {
    const afterStrip = fullPath.slice(0, -DOCUMENTS_FILES_SUFFIX.length);
    const mixedHistory =
      afterStrip.endsWith('/history') ||
      afterStrip.includes('/history/compare/');
    if (mixedHistory) {
      let basePath: string | undefined;
      if (afterStrip.includes('/history/compare/')) {
        const compareMatch = afterStrip.match(/^(.+)\/history\/compare\/.+/);
        basePath = compareMatch ? compareMatch[1] : undefined;
      } else if (afterStrip.endsWith('/history')) {
        basePath = afterStrip.slice(0, -'/history'.length);
      }

      if (basePath) {
        return {
          documentPath: basePath,
          isHistoryMode: false,
          isFilesPage: true,
        };
      }
    }

    return {
      documentPath: afterStrip,
      isHistoryMode: false,
      isFilesPage: true,
    };
  }

  if (fullPath.includes('/history/compare/')) {
    const compareMatch = fullPath.match(/^(.+)\/history\/compare\/.+/);
    const documentPath = compareMatch ? compareMatch[1] : undefined;
    if (documentPath) {
      return {
        documentPath,
        isHistoryMode: true,
        isFilesPage: false,
      };
    }
  }

  const isHistoryMode = fullPath.endsWith('/history');
  let documentPath: string | undefined;
  if (isHistoryMode) {
    documentPath = fullPath.slice(0, -'/history'.length);
  } else {
    documentPath = fullPath || undefined;
  }

  return {
    documentPath,
    isHistoryMode,
    isFilesPage: false,
  };
}

/**
 * Map a legacy `/documents/...` pathname to the new `/d/...` canonical pathname
 * (no query or hash — caller may append).
 */
export function legacyPathnameToAppPathname(legacy: string): string {
  if (legacy === `${LEGACY_PREFIX}/` || legacy === LEGACY_PREFIX) {
    return `${APP_PREFIX}`;
  }

  const m = legacy.match(new RegExp(`^${escapeRegex(LEGACY_PREFIX)}/(.+)$`));
  if (!m) {
    return legacy.startsWith(LEGACY_PREFIX) ? `${APP_PREFIX}` : legacy;
  }

  const fullPath = m[1]!;
  const r = parseDocumentLocationSuffix(fullPath);

  if (r.isFilesPage && r.documentPath != null) {
    return `${APP_PREFIX}/files/${encodeDocumentPathForUrl(r.documentPath)}`;
  }

  if (r.isHistoryMode && r.documentPath != null) {
    if (fullPath.includes('/history/compare/')) {
      return `${APP_PREFIX}/history/${encodePathPreservingSlashes(fullPath)}`;
    }

    return `${APP_PREFIX}/history/${encodeDocumentPathForUrl(r.documentPath)}`;
  }

  if (r.documentPath != null) {
    return `${APP_PREFIX}/view/${encodeDocumentPathForUrl(r.documentPath)}`;
  }

  return `${APP_PREFIX}`;
}

/**
 * `encodeURIComponent` per segment, but re-applies a path that was stored as
 * a single legacy "fullPath" (may contain slashes).
 */
function encodePathPreservingSlashes(path: string): string {
  return path
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/**
 * After `/d/history/…`, the tail is either a plain document path or the same
 * legacy suffix shape (ending with /history, or with /history/compare/…).
 */
export function parseAppHistorySplat(historyTail: string): {
  documentPath?: string;
  isHistoryMode: boolean;
  isFilesPage: boolean;
} {
  if (
    historyTail.includes('/history/compare/') ||
    (historyTail.length > '/history'.length && historyTail.endsWith('/history'))
  ) {
    return parseDocumentLocationSuffix(historyTail);
  }

  return {
    documentPath: historyTail || undefined,
    isHistoryMode: true,
    isFilesPage: false,
  };
}

/** Splat from `d/view/*` (decoded document path, may include `/`). */
export function getDocumentPathFromViewSplat(
  splat: string | undefined,
): string {
  if (splat == null || splat === '') {
    return '';
  }

  return decodeSplat(splat);
}

/** Splat from `d/files/*` (decoded document path). */
export function getDocumentPathFromFilesSplat(
  splat: string | undefined,
): string {
  if (splat == null || splat === '') {
    return '';
  }

  return decodeSplat(splat);
}

function decodeSplat(splat: string): string {
  if (!splat) return splat;

  return splat
    .split('/')
    .map((seg) => {
      try {
        return decodeURIComponent(seg);
      } catch {
        return seg;
      }
    })
    .join('/');
}

export function documentListHref(): string {
  return APP_PREFIX;
}

export function documentViewHref(path: string): string {
  return `${APP_PREFIX}/view/${encodeDocumentPathForUrl(path)}`;
}

export function documentHistoryHref(path: string): string {
  return `${APP_PREFIX}/history/${encodeDocumentPathForUrl(path)}`;
}

export function documentFilesHref(path: string): string {
  return `${APP_PREFIX}/files/${encodeDocumentPathForUrl(path)}`;
}
